export type Role = "cashier" | "supervisor" | "manager";
export type Method = "cash" | "momo" | "card" | "credit" | "account";

/** Till staff roles: the selling roles, plus yard staff (only the Yard screen). */
export type StaffRole = Role | "yard";
export type StaffLite = { id: string; name: string; role: StaffRole; color: string; hasPin: boolean; active?: boolean; lastSeenAt?: number | null; week?: number };
export type TillInfo = { id: string; name: string; number: number; pairedBy?: string | null; pairedAt?: number };

export type TillSettingsPublic = {
  methods: Method[];
  requireRef: Method[];
  momoMerchant: string;
  receiptHeader: string;
  receiptFooter: string;
  returnDays: number;
  discountLimit: Record<Role, number>;
  refundLimit: Record<Role, number | null>;
  overShortLimit: number;
  allowNegativeStock: boolean;
  paystackEnabled?: boolean;
  manualFallback?: boolean;
  displayMode?: "sale" | "pick" | "pay";
  selfPayScope?: "all" | "accessories";
  displayShowStock?: boolean;
  displayHiddenCategories?: string[];
  autoPrint?: boolean;
  groupTier?: Record<Group, Tier>;
  walkInWholesale?: boolean;
  wholesaleNoDiscount?: boolean;
  offlineSales?: boolean;
  /** Hours a till may sell offline before it waits for the line; 0 = no limit. */
  offlineMaxHours?: number;
  offlineSerial?: boolean;
  websiteImport?: boolean;
  serials?: boolean;
  delivery?: { enabled: boolean };
};

export type Tier = "retail" | "wholesale" | "distributor";
export type Group = "retail" | "wholesale" | "distributor" | "staff";
export const GROUP_LABEL: Record<Group, string> = { retail: "Retail", wholesale: "Wholesale", distributor: "Distributor", staff: "Staff" };
export type ShopCustomer = { id: string; phone: string; name: string | null; email: string | null; group: Group; creditLimit: number | null; note: string | null; balance?: number; termsDays?: number; buyers?: { name: string; limit: number | null }[] };
export type PriceBreak = { minQty: number; price: number };

export type Item = {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  category: string;
  brand: string;
  image: string | null;
  price: number;
  cost: number | null;
  tracking: "qty" | "serial" | "service";
  onHand: number | null;
  reorderAt: number;
  warrantyMonths: number | null;
  webSlug: string | null;
  webVariantId: string | null;
  sync: boolean;
  active: boolean;
  webPrice?: number | null;
  webStock?: number | null;
  webMissing?: boolean;
  wholesalePrice?: number | null;
  wholesaleMin?: number | null;
  distributorPrice?: number | null;
  distributorMin?: number | null;
  breaks?: PriceBreak[];
  /** What one is called on prices and stock ("bag", "length"); none = pieces. */
  unit?: string | null;
  /** Bigger packs the till sells as many units. */
  packs?: { name: string; qty: number }[];
};

export type SaleLine = {
  itemId: string;
  name: string;
  sku: string | null;
  tracking: string;
  qty: number;
  price: number;
  listPrice?: number;
  /** retail | wholesale | distributor | break-N | override */
  tier?: string;
  discount: number;
  serials: string[];
  warrantyMonths: number | null;
  refundedQty?: number;
  refundedSerials?: string[];
};
export type Payment = { method: Method; amount: number; ref?: string | null; last4?: string | null; tendered?: number | null; change?: number | null; intentId?: string | null; paidWith?: string | null; manual?: boolean; offlineManual?: boolean };

export type Sale = {
  id: string;
  number: string;
  tillId: string;
  shiftId: string | null;
  staffId: string;
  staffName: string;
  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  lines: SaleLine[];
  payments: Payment[];
  subtotal: number;
  discount: number;
  discountNote: string | null;
  approvedBy: string | null;
  total: number;
  refunded: number;
  status: string;
  offline: boolean;
  pricingTier?: string;
  customerId?: string | null;
  createdAt: number;
};

export type ShiftSummary = {
  sales: number;
  takings: number;
  discounts: number;
  devices: number;
  /** Deliveries and yard collections booked on the shift, and the delivery fees charged. */
  deliveries?: { deliver: number; collect: number; fees: number };
  refunds: number;
  refundTotal: number;
  byMethod: Record<string, { count: number; amount: number }>;
  /** MoMo / card confirmed by Paystack, and any recorded by hand. */
  paystack?: { count: number; amount: number };
  manual?: { count: number; amount: number };
  offline?: { count: number; amount: number };
  unchecked?: { sale: string; method: string; amount: number; ref: string | null; staff: string; at: number }[];
  byTier?: Record<"retail" | "breaks" | "wholesale" | "walkin", { count: number; amount: number; belowRetail: number }>;
  walkIns?: { sale: string; name: string | null; approvedBy: string | null; amount: number; retail: number }[];
  cashSales: number;
  cashSalesCount: number;
  cashRefunds: number;
  cashRefundsCount: number;
  drops: number;
  paidIn: number;
  paidOut: number;
  drawer: { kind: string; amount: number; reason: string | null; at: number }[];
  openingFloat: number;
  expected: number;
};

export type ZReport = {
  id: string;
  number: string;
  tillId: string;
  tillName: string | null;
  staffName: string;
  openedAt: number;
  closedAt: number | null;
  openingFloat: number;
  expected: number | null;
  counted: number | null;
  difference: number | null;
  counts: Record<string, number>;
  summary: ShiftSummary | null;
  note: string | null;
  approvedBy: string | null;
  status: string;
};

export type Move = { id: string; itemId: string; itemName: string | null; kind: string; qty: number; balance: number; serial: string | null; staffName: string | null; ref: string | null; note: string | null; at: number };

export const METHOD_LABEL: Record<Method, string> = { cash: "Cash", momo: "Mobile Money", card: "Card", credit: "Store credit", account: "On account" };
export const ROLE_LABEL: Record<StaffRole, string> = { cashier: "Cashier", supervisor: "Supervisor", manager: "Manager", yard: "Yard staff" };
const RANK: Record<Role, number> = { cashier: 0, supervisor: 1, manager: 2 };
export const atLeast = (role: string | undefined, min: Role) => (RANK[role as Role] ?? -1) >= RANK[min];
